import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { HaleWordmark } from '../../components/HaleBrand';
import HaleHead from '../../components/hale/HaleHead';
import {
  HALE_BG, HALE_TEXT_MUTED, HALE_TEXT_FAINT, HALE_INK, HALE_HAIR, HALE_RED,
  HALE_NEUTRAL, HALE_DISPLAY, HALE_BODY, HALE_MONO,
  CATEGORIES, RETAILERS, PACKAGING_MATERIALS, SUSTAINABILITY_DECLS,
} from './_design';
import {
  StepHeader, Field, HaleInput, HaleTextarea, ChipGrid, Stepper, Chip,
} from './_intake_fields';
import TI from '../../components/brand/TI';

const API = process.env.REACT_APP_BACKEND_URL;

/* Draft-resume session storage.
 *
 * The previous build kept the draft id + edit_token in `localStorage`,
 * which (a) persists across browser sessions, widening the window an
 * XSS-injected script can lift the token, and (b) leaks across tabs.
 * We move to `sessionStorage` (cleared on tab close), enforce a 24h
 * TTL on persisted sessions, and centralise read/write so the same
 * safety net applies everywhere. */
const SS_KEY = 'hale_app_session_v2';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/* Hale application header — Hale-branded chrome shared by the apply wizard
 * and the splash. Wordmark links home, "Return to Hale Home" link, and a
 * restrained scientific-review partner lockup. Matches the Hale landing +
 * console header treatment (white, blur, hairline border). */
function ApplyHeader({ step, total, showStepper = false }) {
  return (
    <header className="sticky top-0 z-40" style={{ backgroundColor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${HALE_HAIR}` }}>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="hale-apply-home">
          <HaleWordmark size="sm" />
          <span className="hidden sm:flex items-center gap-1.5 pl-3 ml-1 border-l" style={{ borderColor: HALE_HAIR }}>
            <span className="text-[9px] uppercase tracking-[0.18em]" style={{ color: HALE_TEXT_FAINT, fontFamily: HALE_MONO }}>Brand Application</span>
          </span>
        </Link>

        {showStepper && (
          <div className="hidden md:block">
            <Stepper current={step} total={total} />
          </div>
        )}

        <div className="flex items-center gap-4 flex-shrink-0">
          {showStepper && (
            <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: HALE_TEXT_FAINT, fontFamily: HALE_MONO }} data-testid="hale-apply-stepcount">
              Step {step} / {total}
            </span>
          )}
          <Link to="/" className="text-[12px] font-medium" style={{ color: HALE_INK }} data-testid="hale-apply-return">
            Return to Hale Home
          </Link>
        </div>
      </div>
    </header>
  );
}

/* Footer attribution shared by the apply flow + confirmation screen. */
function HalePlatformFootnote() {
  return (
    <p
      className="text-[10px] mt-6 text-center px-6 max-w-3xl mx-auto leading-relaxed"
      style={{ color: HALE_TEXT_FAINT }}
      data-testid="hale-platform-footnote"
    >
      HALE onboarding is powered by <TI color="#E63946" />. Scientific audit outputs are
      routed through the <TI color="#E63946" /> review infrastructure.
    </p>
  );
}

function readDraftSession() {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id || !parsed?.token) return null;
    if (typeof parsed.savedAt !== 'number' || (Date.now() - parsed.savedAt) > SESSION_TTL_MS) {
      sessionStorage.removeItem(SS_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(SS_KEY);
    return null;
  }
}

function writeDraftSession(id, token) {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify({ id, token, savedAt: Date.now() }));
  } catch {
    /* sessionStorage may be unavailable (private mode); resume just won't work. */
  }
}

function clearDraftSession() {
  try { sessionStorage.removeItem(SS_KEY); } catch { /* noop */ }
  // Also clear any legacy localStorage entry from older builds so the
  // resume path doesn't fall back to it inadvertently.
  try { localStorage.removeItem('hale_app_session_v1'); } catch { /* noop */ }
}

const TOTAL_STEPS = 4;

// Module-level initial form state. Hoisted out so the resume effect can
// reference a stable identity instead of closing over component state.
const INITIAL_DATA = {
  founder: { full_name: '', role: '', email: '' },
  brand: { brand_name: '', website: '', country: '', founded_year: null, brand_story: '' },
  products: { categories: [], sku_count: 0, retailer_goals: [], distribution: [], launch_window: '' },
  claims: { has_marketing_claims: false, claims_examples: '', fragrance_disclosure: '', ingredient_transparency: '' },
  packaging: { materials: [], pcr_content: '', refillable: false, fsc_certified: false },
  sustainability: { declarations: [], impact_narrative: '' },
};

/* /hale/apply — premium 4-step intake wizard.
 *
 * Step 1 — Founder + brand identity (anonymous email captured first so the
 *          rest of the form can be autosaved by an opaque draft id).
 * Step 2 — Portfolio: categories, SKU count, retailer ambition, distribution.
 * Step 3 — Claims & ingredient posture (Reviewed signals).
 * Step 4 — Packaging & sustainability (Verified signals).
 *
 * Autosaves every change. On submit, the backend runs the eligibility engine
 * and routes to /hale/pathway/<id> where the founder sees their recommended
 * standards track + rationale.
 */
export default function HaleApplyPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [draftId, setDraftId] = useState(null);
  const [editToken, setEditToken] = useState(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(INITIAL_DATA);
  const saveTimer = useRef(null);

  // Resume an in-flight draft from sessionStorage so an accidental
  // refresh doesn't lose the founder's work. Session storage scope means
  // closing the tab clears the resume token (the previous localStorage
  // build kept it forever, widening the XSS blast radius).
  useEffect(() => {
    const session = readDraftSession();
    if (!session) return;
    let cancelled = false;
    axios.get(`${API}/api/hale-marketplace/applications/draft/${session.id}`, {
      headers: { 'X-Edit-Token': session.token },
    }).then(r => {
      if (cancelled) return;
      if (r.data?.status === 'draft' || r.data?.status === 'pending_revision') {
        setDraftId(session.id);
        setEditToken(session.token);
        setEmail(r.data.email || '');
        setData(r.data.draft || INITIAL_DATA);
        setStep(r.data.current_step || 1);
      } else {
        clearDraftSession();
      }
    }).catch(() => {
      if (!cancelled) clearDraftSession();
    });
    return () => { cancelled = true; };
  }, []);

  // Debounced autosave whenever the form data or current step changes.
  useEffect(() => {
    if (!draftId || !editToken) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.patch(`${API}/api/hale-marketplace/applications/draft/${draftId}`, {
          current_step: step, draft: data,
        }, { headers: { 'X-Edit-Token': editToken } });
      } catch (e) {
        // Autosave is intentionally non-blocking — log for diagnostics but
        // never interrupt the founder's typing flow.
        console.warn('HALE autosave failed (will retry on next keystroke):', e);
      }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [data, step, draftId, editToken]);

  const startDraft = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      const r = await axios.post(`${API}/api/hale-marketplace/applications/draft`, { email });
      setDraftId(r.data.id);
      setEditToken(r.data.edit_token);
      writeDraftSession(r.data.id, r.data.edit_token);
      // Seed the founder email so it shows up downstream.
      setData(d => ({ ...d, founder: { ...d.founder, email } }));
      setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start application');
    }
  };

  const next = () => setStep(s => Math.min(TOTAL_STEPS, s + 1));
  const prev = () => setStep(s => Math.max(1, s - 1));
  const update = (section, patch) =>
    setData(d => ({ ...d, [section]: { ...d[section], ...patch } }));
  const toggleArr = (section, key, value) =>
    setData(d => {
      const arr = d[section][key] || [];
      const has = arr.includes(value);
      return { ...d, [section]: { ...d[section], [key]: has ? arr.filter(x => x !== value) : [...arr, value] } };
    });

  const submit = async () => {
    if (!data.founder?.full_name?.trim()) { toast.error('Founder name is required'); setStep(1); return; }
    if (!data.brand?.brand_name?.trim()) { toast.error('Brand name is required'); setStep(1); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/api/hale-marketplace/applications/submit/${draftId}`,
        {}, { headers: { 'X-Edit-Token': editToken } },
      );
      clearDraftSession();
      // Pass recommendation via state for instant render.
      navigate(`/hale/pathway/${draftId}`, {
        state: { recommendation: r.data.recommendation, magic_link: r.data.magic_link },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
      setSubmitting(false);
    }
  };

  if (!draftId) return <Splash email={email} setEmail={setEmail} onStart={startDraft} />;

  return (
    <div className="min-h-screen" style={{ backgroundColor: HALE_BG, color: HALE_INK, fontFamily: HALE_BODY }}>
      <HaleHead title="Apply · The Hale League" />
      <ApplyHeader step={step} total={TOTAL_STEPS} showStepper />

      {/* Mobile stepper row */}
      <div className="md:hidden border-b" style={{ borderColor: HALE_HAIR, backgroundColor: HALE_NEUTRAL }}>
        <div className="max-w-3xl mx-auto px-5 py-3 flex justify-center">
          <Stepper current={step} total={TOTAL_STEPS} />
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-0 py-12 lg:py-16">
        <div className="rounded-2xl border p-6 sm:p-9" style={{ borderColor: HALE_HAIR, backgroundColor: '#FFFFFF' }} data-testid="hale-apply-card">
          {step === 1 && <Step1 data={data} update={update} />}
          {step === 2 && <Step2 data={data} update={update} toggleArr={toggleArr} />}
          {step === 3 && <Step3 data={data} update={update} />}
          {step === 4 && <Step4 data={data} update={update} toggleArr={toggleArr} />}

          {/* Footer nav */}
          <div className="flex items-center justify-between mt-12 pt-7 border-t" style={{ borderColor: HALE_HAIR }}>
            {step > 1 ? (
              <Button onClick={prev} variant="ghost"
                className="text-[#71717A] hover:text-[#111214] text-[13px] font-semibold hover:bg-transparent px-0"
                data-testid="apply-prev">
                ← Back
              </Button>
            ) : <span />}
            {step < TOTAL_STEPS ? (
              <Button onClick={next}
                className="h-11 px-6 text-[13px] font-semibold rounded-lg bg-[#E63946] hover:bg-[#C9192C] text-white inline-flex items-center gap-2"
                data-testid="apply-next">
                Continue <ArrowRight size={15} weight="bold" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting}
                className="h-11 px-6 text-[13px] font-semibold rounded-lg bg-[#E63946] hover:bg-[#C9192C] text-white inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="apply-submit">
                {submitting ? 'Submitting…' : <>Submit Application <ArrowRight size={15} weight="bold" /></>}
              </Button>
            )}
          </div>
        </div>

        <p className="text-[11px] mt-6 text-center uppercase tracking-[0.18em]" style={{ color: HALE_TEXT_FAINT, fontFamily: HALE_MONO }}>
          Autosaved · resume anytime from this device
        </p>

        {/* Platform attribution — quiet footer for the apply flow. */}
        <HalePlatformFootnote />

        {/* Reliance scope — evidence-supported governance positioning */}
        <p className="text-[11px] mt-6 text-center px-2 max-w-3xl mx-auto leading-relaxed"
           style={{ color: HALE_TEXT_FAINT }} data-testid="hale-reliance-notice">
          HALE Reviewed™ and HALE Verified™ are evidence-supported scientific governance workflows
          designed to help modern men&apos;s brands stay ahead of evolving retailer and documentation
          expectations. Reviews rely on documentation provided by brands, suppliers, manufacturers,
          and laboratories, and reflect information available at the time of evaluation.
          ToxIntel and Hale facilitate structured documentation review;
          they do not independently warrant product safety, regulatory compliance, or retailer acceptance.
        </p>
      </main>
    </div>
  );
}

function Splash({ email, setEmail, onStart }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: HALE_BG, color: HALE_INK, fontFamily: HALE_BODY }}>
      <HaleHead title="Apply · The Hale League" />
      <ApplyHeader />
      <main className="flex-1 flex items-center justify-center px-5 py-12 sm:py-16">
        <div className="w-full max-w-lg">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] mb-4" style={{ color: HALE_RED, fontFamily: HALE_MONO }}>
            The Hale League · Brand Application
          </p>
          <h1 className="font-black leading-[1.06] tracking-[-0.02em] text-[clamp(1.9rem,4.5vw,2.8rem)]"
              style={{ fontFamily: HALE_DISPLAY, color: HALE_INK }} data-testid="hale-apply-splash-title">
            Apply to bring your brand into the League.
          </h1>
          <p className="text-[15px] mt-5 leading-relaxed" style={{ color: HALE_TEXT_MUTED }}>
            A focused, ~6 minute intake. Tell us about your brand, portfolio, claims, and packaging.
            The Hale review team uses it to match you with the right partners and scientific review pathway.
          </p>

          <div className="mt-9 rounded-2xl border p-6 sm:p-7" style={{ borderColor: HALE_HAIR, backgroundColor: '#FFFFFF' }}>
            <label className="block">
              <span className="text-[12px] font-semibold" style={{ color: HALE_INK }}>Founder email</span>
              <HaleInput value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5"
                type="email" placeholder="you@yourbrand.com" data-testid="apply-email" />
            </label>
            <Button onClick={onStart}
              className="mt-5 w-full sm:w-auto h-11 px-6 text-[13px] font-semibold rounded-lg bg-[#E63946] hover:bg-[#C9192C] text-white inline-flex items-center justify-center gap-2"
              data-testid="apply-start">
              Begin application <ArrowRight size={15} weight="bold" />
            </Button>
            <p className="text-[11px] mt-5 uppercase tracking-[0.16em]" style={{ color: HALE_TEXT_FAINT, fontFamily: HALE_MONO }}>
              ~6 minutes · autosaved · access granted after review
            </p>
            <p className="text-[12.5px] mt-3 leading-relaxed" style={{ color: HALE_TEXT_MUTED }} data-testid="apply-helper">
              Submit your brand intake first. Portal access is granted after Hale review.
              Already onboarded? <Link to="/hale/login" className="font-semibold" style={{ color: HALE_RED }} data-testid="apply-signin-link">Brand Sign In</Link>.
            </p>
          </div>

          <p className="text-[10px] mt-7 uppercase tracking-[0.18em]" style={{ color: HALE_TEXT_FAINT, fontFamily: HALE_MONO }} data-testid="hale-powered-by-hero">
            Scientific review powered by <TI color={HALE_RED} /> + A&#x1d33;
          </p>
        </div>
      </main>
    </div>
  );
}

/* ─────────── Steps ─────────── */
function Step1({ data, update }) {
  const [passport, setPassport] = useState(null);

  /* Try to pre-fetch the brand's Passport. If they have one, we'll show
   * a "Import from Passport™" button that fills the brand fields in
   * one click — the "Collect Once · Reuse Everywhere" principle. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/passport/hale-prefill`, { withCredentials: true });
        if (!cancelled) setPassport(r.data);
      } catch (e) {
        // Passport pre-fill is opt-in — anonymous users hit 401 here which is
        // expected. Log other errors but never block the apply flow.
        if (e?.response?.status && e.response.status !== 401) {
          console.warn('Passport pre-fill failed:', e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const importFromPassport = () => {
    if (!passport?.profile) return;
    const p = passport.profile;
    update('brand', {
      brand_name: p.brand_name || data.brand.brand_name,
    });
    toast.success(
      `Imported from your Passport™ · ${passport.summary?.total_evidence || 0} reusable evidence items detected`,
      { duration: 5000 },
    );
  };

  return (
    <>
      <StepHeader kicker="Step 01 · Founder & brand"
        title="Tell us who's behind the brand."
        sub="The HALE marketplace is a high-trust environment. Founders matter as much as product." />

      {/* Passport reuse banner */}
      {passport?.profile?.brand_name && (
        <div className="mb-6 p-3 border border-[#E63946]/30 bg-[#FDE8E8]/40 rounded-sm flex items-center justify-between gap-3" data-testid="passport-reuse-banner">
          <div className="text-[12px] text-[#4B5563]">
            <span className="font-bold text-[#0B2447]"><TI color="#E63946" /> Passport™ detected.</span>{' '}
            We can prefill brand info + reuse {passport.summary?.total_evidence || 0} evidence item(s) from your Passport.
          </div>
          <button onClick={importFromPassport}
            className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 bg-[#0B2447] hover:bg-[#0B2447]/90 text-white rounded-sm flex-shrink-0"
            data-testid="passport-import-btn">
            Import from Passport™
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
        <Field label="Full name"><HaleInput data-testid="f-founder-name"
          value={data.founder.full_name} onChange={e => update('founder', { full_name: e.target.value })} /></Field>
        <Field label="Title / role"><HaleInput data-testid="f-founder-role"
          value={data.founder.role || ''} onChange={e => update('founder', { role: e.target.value })} /></Field>
        <Field label="Brand name"><HaleInput data-testid="f-brand-name"
          value={data.brand.brand_name} onChange={e => update('brand', { brand_name: e.target.value })} /></Field>
        <Field label="Website / handle" optional><HaleInput data-testid="f-brand-website"
          value={data.brand.website || ''} onChange={e => update('brand', { website: e.target.value })} placeholder="yourbrand.com" /></Field>
        <Field label="Headquarters"><HaleInput data-testid="f-brand-country"
          value={data.brand.country || ''} onChange={e => update('brand', { country: e.target.value })} placeholder="United States" /></Field>
        <Field label="Founded" optional><HaleInput type="number" data-testid="f-brand-year"
          value={data.brand.founded_year || ''} onChange={e => update('brand', { founded_year: e.target.value ? parseInt(e.target.value) : null })} /></Field>
        <Field label="Brand story" full
          hint="Two or three sentences. The HALE review team reads this — write it as you would in a Vogue feature, not a pitch deck.">
          <HaleTextarea rows={4} data-testid="f-brand-story"
            value={data.brand.brand_story || ''} onChange={e => update('brand', { brand_story: e.target.value })} />
        </Field>
      </div>
    </>
  );
}

function Step2({ data, update, toggleArr }) {
  return (
    <>
      <StepHeader kicker="Step 02 · Portfolio"
        title="What are you bringing to the marketplace?"
        sub="Categories, scale, and retailer ambition route you to the right standards pathway." />
      <Field label="Product categories" full hint="Select every category you operate in.">
        <ChipGrid options={CATEGORIES} value={data.products.categories}
          onToggle={(k) => toggleArr('products', 'categories', k)} testidPrefix="f-cat" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7 mt-8">
        <Field label="Active SKU count"
          hint="Include in-market + imminent launches.">
          <HaleInput type="number" min="0" data-testid="f-sku-count"
            value={data.products.sku_count} onChange={e => update('products', { sku_count: parseInt(e.target.value) || 0 })} />
        </Field>
        <Field label="Launch window">
          <select data-testid="f-launch-window"
            value={data.products.launch_window || ''} onChange={e => update('products', { launch_window: e.target.value })}
            className="w-full bg-transparent border-0 border-b h-11 text-base px-0 focus:outline-none"
            style={{ borderBottomColor: 'rgba(11,20,38,0.10)', color: HALE_INK }}>
            <option value="">Select…</option>
            <option value="live">Live in market</option>
            <option value="pre_launch">Pre-launch</option>
            <option value="rebrand">Rebrand / re-platform</option>
          </select>
        </Field>
      </div>
      <Field label="Retailer ambition" full
        hint="Which retailers do you want to land — or already partner with? We map your readiness against each.">
        <ChipGrid options={RETAILERS} value={data.products.retailer_goals}
          onToggle={(k) => toggleArr('products', 'retailer_goals', k)} testidPrefix="f-ret" />
      </Field>
      <Field label="Distribution today" full>
        <div className="flex gap-2 flex-wrap">
          {['dtc', 'wholesale', 'retail', 'subscription'].map(k => (
            <Chip key={k} active={(data.products.distribution || []).includes(k)}
              onClick={() => toggleArr('products', 'distribution', k)}
              testid={`f-dist-${k}`}>
              {k === 'dtc' ? 'Direct-to-consumer' : k.charAt(0).toUpperCase() + k.slice(1)}
            </Chip>
          ))}
        </div>
      </Field>
    </>
  );
}

function Step3({ data, update }) {
  return (
    <>
      <StepHeader kicker="Step 03 · Claims & ingredients"
        title="How do you talk about your formulas?"
        sub="Drives the HALE Reviewed™ scientific defensibility pathway." />
      <div className="space-y-8">
        <Field label="Marketing claims posture">
          <div className="flex gap-2 flex-wrap mt-1">
            <Chip active={data.claims.has_marketing_claims === true}
              onClick={() => update('claims', { has_marketing_claims: true })} testid="f-claims-yes">
              Yes — we make functional claims
            </Chip>
            <Chip active={data.claims.has_marketing_claims === false}
              onClick={() => update('claims', { has_marketing_claims: false })} testid="f-claims-no">
              No / minimal claims
            </Chip>
          </div>
        </Field>
        {data.claims.has_marketing_claims && (
          <Field label="Examples of your strongest claim language" full optional
            hint="Just paste a few — the reviewer will assess defensibility.">
            <HaleTextarea rows={3} data-testid="f-claims-examples"
              value={data.claims.claims_examples || ''}
              onChange={e => update('claims', { claims_examples: e.target.value })} />
          </Field>
        )}
        <Field label="Fragrance disclosure">
          <div className="flex gap-2 flex-wrap mt-1">
            {[['fully', 'Fully disclosed'], ['partial', 'Allergens only'],
              ['none', 'Proprietary'], ['no_fragrance', 'Fragrance-free']].map(([k, l]) => (
              <Chip key={k} active={data.claims.fragrance_disclosure === k}
                onClick={() => update('claims', { fragrance_disclosure: k })} testid={`f-frag-${k}`}>{l}</Chip>
            ))}
          </div>
        </Field>
        <Field label="Ingredient transparency">
          <div className="flex gap-2 flex-wrap mt-1">
            {[['full_inci', 'Full INCI'], ['key_actives', 'Key actives'],
              ['proprietary', 'Proprietary blend']].map(([k, l]) => (
              <Chip key={k} active={data.claims.ingredient_transparency === k}
                onClick={() => update('claims', { ingredient_transparency: k })} testid={`f-ing-${k}`}>{l}</Chip>
            ))}
          </div>
        </Field>
      </div>
    </>
  );
}

function Step4({ data, update, toggleArr }) {
  return (
    <>
      <StepHeader kicker="Step 04 · Packaging & sustainability"
        title="What does your stewardship look like?"
        sub="Drives the HALE Verified™ sustainability pathway." />
      <Field label="Packaging materials" full hint="Select every material in your active line.">
        <ChipGrid options={PACKAGING_MATERIALS} value={data.packaging.materials}
          onToggle={(k) => toggleArr('packaging', 'materials', k)} testidPrefix="f-pkg-mat" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7 mt-8">
        <Field label="PCR content">
          <div className="flex gap-2 flex-wrap mt-1">
            {[['none', 'None'], ['<30', '< 30%'], ['30-60', '30 – 60%'], ['>60', '> 60%']].map(([k, l]) => (
              <Chip key={k} active={data.packaging.pcr_content === k}
                onClick={() => update('packaging', { pcr_content: k })} testid={`f-pcr-${k}`}>{l}</Chip>
            ))}
          </div>
        </Field>
        <Field label="Refill / certifications">
          <div className="flex gap-2 flex-wrap mt-1">
            <Chip active={!!data.packaging.refillable}
              onClick={() => update('packaging', { refillable: !data.packaging.refillable })} testid="f-refill">Refillable</Chip>
            <Chip active={!!data.packaging.fsc_certified}
              onClick={() => update('packaging', { fsc_certified: !data.packaging.fsc_certified })} testid="f-fsc">FSC-certified paper</Chip>
          </div>
        </Field>
      </div>
      <Field label="Supplier-supported sustainability declarations" full
        hint="Select declarations you can substantiate with supplier documentation. Each selection requires supporting evidence (supplier letter, certification, or test report) — upload via your ToxIntel Passport™.">
        <ChipGrid options={SUSTAINABILITY_DECLS} value={data.sustainability.declarations}
          onToggle={(k) => toggleArr('sustainability', 'declarations', k)} testidPrefix="f-sust" />
      </Field>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mt-3 pl-1">
        <Link to="/passport" className="text-[#E63946] hover:underline">
          → Upload supporting evidence in Passport™
        </Link>
      </div>
      <Field label="Impact narrative" full optional
        hint="Where you're going. Aspirational + measurable.">
        <HaleTextarea rows={3} data-testid="f-impact-narrative"
          value={data.sustainability.impact_narrative || ''}
          onChange={e => update('sustainability', { impact_narrative: e.target.value })} />
      </Field>
    </>
  );
}
